import React, { useMemo, useRef } from 'react'
import { Table as AntTable, TablePaginationConfig } from 'antd'
import 'antd/lib/table/style/'
import './Table.scss'
import useElementMeasurements from 'core/hooks/useElementMeasurements'
import {
  DataType,
  TableStateType,
  TableType
} from 'core/components/data/table/TableTypes'
import LoadingStateOverlay from 'core/components/data/table/LoadingStateOverlay'
import EmptyStateOverlay from 'core/components/data/table/EmptyStateOverlay'
import Icon from 'core/components/icons/Icon'
import cn from 'classnames'
import ErrorStateOverlay from './ErrorStateOverlay'
import useUrlSearch from 'core/hooks/useUrlSearch'

const DefaultEmptyConfig: TableStateType = {
  icon: <Icon name="info" className="text-dark-1" />,
  title: 'No records to show',
  subtitle: ''
}

const DefaultErrorConfig: TableStateType = {
  icon: (
    <Icon
      name="exclamation-mark"
      className="text-2xl border border-dark-2 rounded-full"
    />
  ),
  title: 'Something went wrong',
  subtitle: 'Please try again later'
}

const Table = <T extends DataType>({
  data,
  columns,
  onRowClick,
  rowKey = 'token_id',
  pagination,
  emptyState = DefaultEmptyConfig,
  errorState = DefaultErrorConfig,
  status,
  columnsOrder,
  disableHover,
  scroll,
  highlightRow,
  notAllowedRows,
  ...props
}: TableType<T>) => {
  const parentRef = useRef<HTMLDivElement>(null)
  const { width: parentWidth } = useElementMeasurements(parentRef)
  const [urlParams] = useUrlSearch()
  //Selected Table Row
  const urlSelectedRow = urlParams.get('t_sr')
  const paginationConfig = useMemo((): TablePaginationConfig | false => {
    if (!pagination) return false

    return {
      ...pagination,
      onShowSizeChange: (_, size) => {
        pagination?.onSizeChange?.(size)
      },
      showTotal: () => {
        const { current, pageSize, total, totalPages } = pagination
        if (
          current === undefined ||
          pageSize === undefined ||
          total === undefined
        ) {
          return ''
        }

        let totalsLabel = ''

        if (current === totalPages) {
          totalsLabel = `Showing ${total} out of ${total}`
        } else {
          totalsLabel = `Showing ${(current ?? 1) * (pageSize ?? 1)} out of ${
            total ?? 1
          }`
        }

        return (
          <div className="flex items-center gap-3">
            {status === 'loading' && (
              <Icon name="loading" color="text-primary" size={16} />
            )}
            <span>{totalsLabel}</span>
          </div>
        )
      }
    }
  }, [pagination, status])

  const fixedColumnsWidth = useMemo((): number => {
    const tHead = parentRef.current?.getElementsByTagName('thead')
    const tColumns = tHead?.[0]?.children?.[0]?.children ?? []
    let combinedWidth = 0
    Array.from(tColumns)?.forEach((col) => {
      if (Array.from(col.classList).includes('ant-table-cell-fix-left')) {
        combinedWidth = combinedWidth + col.scrollWidth
      }
    })
    return combinedWidth
  }, [parentRef])

  const orderedColumns = useMemo(() => {
    const cols = columnsOrder?.length
      ? (columnsOrder
          .map((c) => {
            return (columns as TableType<T>['columns'])?.find(
              // eslint-disable-next-line
              // @ts-ignore
              (col) => col?.dataIndex === c || col?.key === c
            )
          })
          .filter((c) => !!c) as TableType<T>['columns']) ?? []
      : columns ?? []

    return cols
  }, [columns, columnsOrder])

  return (
    <div ref={parentRef}>
      <AntTable
        {...props}
        columns={orderedColumns}
        dataSource={data ?? []}
        rowKey={rowKey}
        pagination={paginationConfig}
        className={cn([props.className, disableHover && 'disable-row-hover'])}
        locale={{
          emptyText:
            status === 'loading' ? (
              <LoadingStateOverlay />
            ) : status === 'error' ? (
              <ErrorStateOverlay errorState={errorState} />
            ) : (
              <EmptyStateOverlay emptyState={emptyState} />
            )
        }}
        showSorterTooltip={{
          target: 'sorter-icon'
        }}
        scroll={{ x: parentWidth + fixedColumnsWidth, ...(scroll ?? {}) }}
        rowClassName={(record) => {
          const _set = new Set(notAllowedRows)
          const shouldDisableHover = !!(
            notAllowedRows?.length && _set.has(record?.token_id)
          )
          if (shouldDisableHover) return 'not-allowed-rows'

          const toHighlight = urlSelectedRow || highlightRow || null
          return !!toHighlight && record?.token_id === toHighlight
            ? 'hovered-row'
            : ''
        }}
        onRow={(record: T) => {
          return {
            onClick: (event) => {
              if (props?.rowSelection?.selectedRowKeys?.length) return
              onRowClick?.(record, event)
            }
          }
        }}
      />
    </div>
  )
}

export default Table
